import type { CalendarDateRange, DateISO } from '@travelata-libs/ui-kit';
import { addDaysToDate } from '@travelata-libs/utils';
import { computed, ref, type Ref } from 'vue';
import {
  clampFlexible,
  countNights,
  fromStayDates,
  getCheckOut,
  getMaxDate,
  getMinDate,
  parseDate,
  toISO,
  toStayDates,
  type StayCriteria,
  type StayDates,
} from '@/entities/searchCriteria';
import { MAX_NIGHTS } from '@/shared/config/nights';
import type { UseStayDates } from './types';
import { formatNights, formatPendingValue, formatStayValue } from '../lib/formatStayLabel';

export interface UseStayDatesOptions {
  model: Ref<StayCriteria>;
}

function toDayIso(iso: string): string {
  return toISO(parseDate(iso));
}

export function useStayDates(options: UseStayDatesOptions): UseStayDates {
  const { model } = options;
  const controlActivated = ref(false);
  const pendingCheckIn = ref<string | null>(null);
  const hoveredDate = ref<string | null>(null);
  const isInvalid = ref(false);

  const stay = computed<StayDates>(() => toStayDates(model.value));

  const hoveredCheckOut = computed<string | null>(() => {
    const checkIn = pendingCheckIn.value;
    const hovered = hoveredDate.value;
    if (!checkIn || !hovered || hovered <= checkIn) {
      return null;
    }
    return hovered;
  });

  const calendarValue = computed<CalendarDateRange<DateISO>>(() => {
    const checkIn = pendingCheckIn.value;
    if (!checkIn) {
      return { start: stay.value.checkIn, end: getCheckOut(stay.value) };
    }
    const hovered = hoveredDate.value;
    if (!hovered) {
      return { start: checkIn, end: checkIn };
    }

    return hovered < checkIn ? { start: hovered, end: checkIn } : { start: checkIn, end: hovered };
  });

  const valueLabel = computed<string>(() =>
    pendingCheckIn.value ? formatPendingValue(pendingCheckIn.value) : formatStayValue(stay.value),
  );

  const hoverNightsLabel = computed<string>(() => {
    const checkIn = pendingCheckIn.value;
    const checkOut = hoveredCheckOut.value;
    if (!checkIn || !checkOut) {
      return '';
    }
    return formatNights(countNights(checkIn, checkOut));
  });

  const minDate = computed<string>(() => toISO(getMinDate()));

  const maxDate = computed<string>(() => toISO(getMaxDate()));

  const disabledFromDate = computed<string | null>(() => {
    if (!pendingCheckIn.value) {
      return null;
    }
    return toISO(addDaysToDate(parseDate(pendingCheckIn.value), MAX_NIGHTS + 1));
  });

  const open = (): void => {
    controlActivated.value = true;
  };

  const close = (): void => {
    controlActivated.value = false;
    hoveredDate.value = null;
    if (pendingCheckIn.value) {
      isInvalid.value = true;
    }
  };

  const toggle = (): void => {
    if (controlActivated.value) {
      close();
      return;
    }
    open();
  };

  const clickDate = (iso: string): boolean => {
    const day = toDayIso(iso);
    const checkIn = pendingCheckIn.value;
    hoveredDate.value = null;

    if (!checkIn || day < checkIn) {
      pendingCheckIn.value = day;
      return false;
    }
    if (day === checkIn) {
      return false;
    }

    model.value = fromStayDates({ checkIn, nights: countNights(checkIn, day), flexible: stay.value.flexible });
    pendingCheckIn.value = null;
    isInvalid.value = false;
    return true;
  };

  const hoverDate = (iso: string | null): void => {
    hoveredDate.value = iso ? toDayIso(iso) : null;
  };

  const setFlexible = (daysFlexible: number): void => {
    const next = daysFlexible === stay.value.flexible ? 0 : clampFlexible(daysFlexible);
    model.value = fromStayDates({ ...stay.value, flexible: next });
  };

  const validate = (): boolean => {
    if (!pendingCheckIn.value) {
      return true;
    }
    isInvalid.value = true;
    controlActivated.value = true;
    return false;
  };

  return {
    controlActivated,
    pendingCheckIn,
    isInvalid,
    stay,
    calendarValue,
    valueLabel,
    hoverNightsLabel,
    minDate,
    maxDate,
    disabledFromDate,
    open,
    close,
    toggle,
    clickDate,
    hoverDate,
    setFlexible,
    validate,
  };
}
